import React, { forwardRef } from "react";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helpText?: string;
  variant?: "default" | "outline" | "filled";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helpText,
      variant = "default",
      size = "md",
      fullWidth = false,
      className = "",
      id,
      required,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helpId = helpText ? `${inputId}-help` : undefined;

    const baseClasses = [
      "border transition-colors duration-200 focus:outline-none focus:ring-2",
      "placeholder-zinc-500",
      fullWidth ? "w-full" : "",
    ];

    const sizeClasses = {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-2 text-base",
      lg: "px-4 py-3 text-lg",
    };

    const variantClasses = {
      default: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-700 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
      outline: error
        ? "border-2 border-red-500 bg-zinc-900 text-white focus:border-red-400 focus:ring-red-500"
        : "border-2 border-zinc-700 bg-zinc-900 text-white focus:border-amber-400 focus:ring-amber-400",
      filled: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-600 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
    };

    const inputClasses = [
      ...baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      props.disabled ? "opacity-50 cursor-not-allowed bg-zinc-800" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={fullWidth ? "w-full" : ""}>
        {label && (
          <label
            htmlFor={inputId}
            className={`block text-sm font-medium mb-1 ${error ? "text-red-400" : "text-amber-400"}`}
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={[errorId, helpId].filter(Boolean).join(" ") || undefined}
          {...props}
        />

        {error && (
          <p id={errorId} className="mt-1 text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        {helpText && !error && (
          <p id={helpId} className="mt-1 text-sm text-zinc-400">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
